import { Pool } from './Pool';
import { Asteroid } from './Asteroid';
import { Particle } from './Particle';
import { Bullet } from './Bullet';
import { Ship } from './Ship';
import Vec2D from './Vec2d';
import { GameCore } from './GameCore.js';
import { CoordinateMapper } from './CoordinateMapper.js';
import { GameEvents } from './GameCoreEvents.js';
import { GameConstants } from './GameConstants.js';
import { AIPlayer } from './AIPlayer.js';
import { AutopilotUI } from './AutopilotUI.js';
import { TouchControls } from './TouchControls.js';

import * as THREE from 'three';
import { OBB } from 'three/examples/jsm/math/OBB';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Vector3, clock, Geometry, Box3, MathUtils } from 'three';
import { ExplodeAnimation } from './ExplodeAnimation';
import { Stars } from './Stars';
import { DropItem } from './DropItem';

import anime from 'animejs/lib/anime.es.js';

import { io } from 'socket.io/client-dist/socket.io';
import { baseURL } from '../../redux/apis/main-api';

export class Game {
	constructor(_canvas, _onGameOver, _onData, _walletAddress) {
		// Store canvas reference
		this.canvas = _canvas;
		
		// Get actual canvas element size from DOM (respects CSS 100% width/100vh height)
		// This ensures canvas matches CSS container size, not window.innerWidth/innerHeight
		let rect = this.canvas.getBoundingClientRect();
		let measuredWidth = rect.width;
		let measuredHeight = rect.height;
		
		// On mobile, getBoundingClientRect() might return 0 or incorrect values before layout completes
		// Use viewport dimensions as fallback, but cap to reasonable mobile sizes
		if (measuredWidth <= 0 || measuredHeight <= 0 || measuredWidth > 5000 || measuredHeight > 5000) {
			// Use viewport dimensions as fallback
			measuredWidth = window.innerWidth;
			measuredHeight = window.innerHeight;
			
			// Cap to reasonable maximums (prevent issues with very large screens)
			const maxWidth = 1920;
			const maxHeight = 1080;
			if (measuredWidth > maxWidth) measuredWidth = maxWidth;
			if (measuredHeight > maxHeight) measuredHeight = maxHeight;
			
			console.warn('Canvas size invalid, using viewport fallback:', measuredWidth, 'x', measuredHeight);
		}
		
		this.screenWidth = measuredWidth;
		this.screenHeight = measuredHeight;
		
		// Detect mobile device early (needed for pixel ratio and delayed resize)
		const isMobile = window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

		console.log('Canvas size:', this.screenWidth, 'x', this.screenHeight);
		//Scene setup
		this.scene = new THREE.Scene();
		this.scene.updateMatrixWorld(true);

		// Fix aspect ratio calculation - should be width/height, not (width/2)/(height/2)
		var aspectRatio = this.screenWidth / this.screenHeight;
		var viewSize = this.screenHeight / aspectRatio;

		let _viewport = {
			viewSize: viewSize,
			aspectRatio: aspectRatio,
			left: (-aspectRatio * viewSize) / 2,
			right: (aspectRatio * viewSize) / 2,
			top: viewSize / 2,
			bottom: -viewSize / 2,
			near: -100,
			far: 1000,
		};

		//this.camera = new THREE.OrthographicCamera(_viewport.left, _viewport.right, _viewport.top, _viewport.bottom, _viewport.near, _viewport.far);

		this.camera = new THREE.PerspectiveCamera(60, aspectRatio, 0.1, 1000); //perspective camera

		//Camera setup
		this.minCameraY = 20;   // Close zoom (smaller field of view)
		this.maxCameraY = 50;   // Far zoom (larger field of view)
		this.currentZoom = 30;  // Default
		this.zoomSpeed = 0.5;
		
		this.camera.position.set(0, this.currentZoom, 0);
		this.camera.up.set(0, 0, -1);
		this.camera.lookAt(0, 0, 0);
		//this.camera.zoom = 9;
		//this.camera.updateProjectionMatrix();

		//Renderer
		this.renderer = new THREE.WebGLRenderer({ canvas: _canvas, alpha: true, antialias: true }); //renderer with transparent backdrop
		/* this.renderer.shadowMap.enabled = true; //enable shadow
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; */
		this.renderer.setSize(this.screenWidth, this.screenHeight);
		
		// Cap devicePixelRatio on mobile to prevent performance issues and size mismatches
		// Most mobile devices have devicePixelRatio of 2-3, but some can be higher
		// Limiting to 2 on mobile helps prevent the canvas from being too large
		const pixelRatio = isMobile ? Math.min(window.devicePixelRatio, 2) : window.devicePixelRatio;
		this.renderer.setPixelRatio(pixelRatio);

		//Calcualte maximum width and height in world px
		const vFOV = (this.camera.fov * Math.PI) / 180;
		const height = 2 * Math.tan(vFOV / 2) * Math.abs(this.camera.position.y);
		const width = height * this.camera.aspect;
		this.maxPos = new THREE.Vector2(width / 2, height / 2);
		
		// Initialize GameCore (after camera setup so we can calculate world bounds)
		const bounds = CoordinateMapper.calculateWorldBounds(this.camera);
		this.gameCore = new GameCore({
			worldWidth: bounds.width,
			worldHeight: bounds.height,
			maxShipHealth: this.maxShipHealth,
			maxOre: this.maxOre
		});
		
		// Set up GameCore event listeners
		this.setupGameCoreEvents();

		// Flag to enable/disable GameCore (for testing)
		this.useGameCore = true;

		//Lights
		/* const directionalLight = new THREE.DirectionalLight(0xffffff, 0.2);
		directionalLight.castShadow = true;
		this.scene.add(directionalLight); */

		const light = new THREE.AmbientLight(0xffffff, 0.3);
		this.scene.add(light);

		const point3 = new THREE.PointLight(0xff4e26, 1);
		point3.position.x = this.maxPos.x;
		point3.position.z = 0;
		point3.position.y = 3;
		this.scene.add(point3);

		const point2 = new THREE.DirectionalLight(0xffffff, 0.7);
		//point2.castShadow = true;
		point2.position.x = 0;
		point2.position.z = 15;
		point2.position.y = 150;
		this.scene.add(point2);

		/* const sphereSize = 1;
		const pointLightHelper = new THREE.PointLightHelper(point, sphereSize);
		this.scene.add(pointLightHelper); */

		//Helpers
		this.doublePI = Math.PI * 2;
		this.onData = _onData;
		this.onGameOver = _onGameOver;
		//this.wallet = _walletAddress;
		this.animationFrameId;
		this.isGameOver = false;
		this.models;

		//game vars
		this.ship;
		this.score = 0;
		this.ore = 0;
		this.totalOreCollected = 0; // Track total ore collected during this session
		this.maxOre = 10;
		this.meteorModels = [];
		this.asteroidColor = 0xcccccc;
		this.maxShipHealth = 200;

		this.shipOnPlatform = false;
		this.oreDropoffTimer;
		this.platformUp = false;

		this.particlePool = [];
		this.particles;
		this.bulletPool;
		this.bullets;
		this.asteroidPool;
		this.asteroids;
		this.dropItemsPool;
		this.dropItems;

		this.hScan;
		this.asteroidVelFactor = 0;

		//keyboard vars
		this.keyLeft = false;
		this.keyUp = false;
		this.keyRight = false;
		this.keyDown = false;
		this.keySpace = false;
		this.keyboardTouched = false;
		this.keyE = false;
		this.keyQ = false;
		
		// Mobile rotation intensity (0.0 to 1.0) - used instead of binary keyLeft/keyRight
		this.rotationIntensity = 0;  // 0 = no rotation, 1.0 = full speed
		this.rotationDirection = 0;  // 1 = rotate left, 0 = none, -1 = rotate right
		
		// Autopilot setup
		this.autopilotEnabled = false;
		this.aiPlayer = null;
		this.autopilotUI = new AutopilotUI(this);
		this.loadingAI = false;
		this.aiDifficulty = 'expert'; // Use 'expert' since that's what we exported
		
		// Action repeat (K=2) - matches training environment
		this.aiActionRepeat = null;  // Current AI action
		this.aiActionRepeatCount = 0;  // Frames since last action selection
		this.ACTION_REPEAT = 2;  // Execute action for 2 frames (matches training)

		//Frames
		this.FRAMES_PER_SECOND = 60; // Valid values are 60,30,20,15,10...
		// Frame timing strategy:
		// - Exact 16.666ms is too strict (causes frame skipping due to browser timing jitter)
		// - 8.333ms works well on desktop (60 FPS) but allows mobile to reach 70-75 FPS
		// - Using 14ms as compromise: forgiving enough to avoid jitter, strict enough to cap ~65-70 FPS
		// 
		// Testing results:
		// Desktop 60Hz: ~60 FPS (monitor capped)
		// Mobile 120Hz: ~65-70 FPS (throttle capped, down from 120 FPS)
		// This ensures AI compatibility (trained at 60 FPS) while allowing slight performance headroom
		this.FRAME_MIN_TIME = 14; // ~71 FPS theoretical max, effectively 60-70 FPS with timing variance
		this.lastFrameTime = 0; // the last frame time
		
		// FPS monitoring for debugging
		this.fpsFrameCount = 0;
		this.fpsStartTime = 0;
		this.currentFPS = 0;
		this.createFPSDisplay();

		//Drops
		this.availableOre = 0;

		//Server
		this.socket = null;
		this.connectingToServer = false;

		/* window.getAnimationFrame =
			window.requestAnimationFrame ||
			window.webkitRequestAnimationFrame ||
			window.mozRequestAnimationFrame ||
			window.oRequestAnimationFrame ||
			window.msRequestAnimationFrame ||
			function (callback) {
				window.setTimeout(callback, 16.6);
			};

		window.stopAnimationFrame = window.cancelAnimationFrame || window.mozCancelAnimationFrame; */

		//canvas = document.getElementById('canvas');
		//this.context = this.canvas.getContext('2d');

		// Setup resize handler to update renderer and camera when window resizes
		this.handleResize = this.handleResize.bind(this);
		window.addEventListener('resize', this.handleResize);
		
		// On mobile, add a delayed resize check to fix any initialization timing issues
		// This ensures the canvas size is correct after React finishes rendering
		if (isMobile) {
			setTimeout(() => {
				this.handleResize();
			}, 100);
			
			// Also check after a longer delay to catch any late layout changes
			setTimeout(() => {
				this.handleResize();
			}, 500);
		}

		let loader = new GLTFLoader();

		let highscore = localStorage.getItem('highscore_2') ? localStorage.getItem('highscore_2') : 0;
		this.onData({ highscore: highscore });

		Promise.all([
			loader.loadAsync('/assets/game_rocket.gltf'),
			loader.loadAsync('/assets/meteor_1.gltf'),
			loader.loadAsync('/assets/bullet.gltf'),
			loader.loadAsync('/assets/ore.gltf'),
			loader.loadAsync('/assets/bullet_1.gltf'),
			loader.loadAsync('/assets/meteor_2.gltf'),
			loader.loadAsync('/assets/meteor_3.gltf'),
			loader.loadAsync('/assets/health.gltf'),
			loader.loadAsync('/assets/platform.gltf'),
		])
			.then((results) => {
				// here the models are returned in deterministic order
				this.models = results;
				//this.scene.add(shipModel.scene);
				this.starsInit();
				this.meteorModels.push(this.models[1].scene);
				this.meteorModels.push(this.models[5].scene);
				this.meteorModels.push(this.models[6].scene);

				this.platform = this.models[8].scene;

				this.keyboardInit();
				
				// Add touch controls for mobile
				this.touchControls = null;
				if (this.isMobileDevice()) {
					this.touchControls = new TouchControls(this);
				}
				
				// Add mouse wheel zoom for desktop
				if (!this.isMobileDevice()) {
					_canvas.addEventListener('wheel', this.handleWheelZoom.bind(this), { passive: false });
				}
				
				this.particleInit();
				this.bulletInit();
				this.asteroidInit();
				this.dropItemsInit();
				this.shipInit(this.models[0].scene);
				this.platformInit();
				
				// DON'T initialize GameCore here - it will be initialized when game starts
				// GameCore.init() is called by resetGame() -> gameCore.reset() -> gameCore.init()
				
				this.onData({ gameLoaded: true });
			})
			.catch((err) => {
				console.log(err);
			});
	}

	connectToServer() {
		const stagingServer = `https://testgame.pocketrockets.space`;
		const liveServer = `https://game.pocketrockets.space`;
		const staging = false;

		if (this.connectingToServer) return;

		if (this.socket && this.socket.connected) {
			console.log('trying to conenct but already connecteds');
			return;
		}

		this.connectingToServer = true;

		// Determine Socket.IO server URL
		let socketUrl;
		if (process.env.NODE_ENV === 'development') {
			// In development, check if we're running on localhost or on a server
			if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
				// Running on a server via nginx - use current protocol and host, but backend port
				const protocol = window.location.protocol;
				const hostname = window.location.hostname;
				socketUrl = `${protocol}//${hostname}`;
			} else {
				// Local development
				socketUrl = 'http://localhost:8655';
			}
		} else {
			// Production
			socketUrl = staging ? stagingServer : liveServer;
		}

		//Connect to server
		console.log(`[Game] Connecting Socket.IO to: ${socketUrl}`);
		this.socket = io(socketUrl, {
			withCredentials: true,
			transports: ['websocket', 'polling'], // Try websocket first, fallback to polling
		});

		this.socket.on('connect', () => {
			console.log(`[Game] Socket.IO connected! Socket ID: ${this.socket.id}`);
			this.onData({ server: true });
			this.connectingToServer = false;
		});

		this.socket.on('disconnect', (reason) => {
			console.warn(`[Game] Socket.IO disconnected! Reason: ${reason}`);
			this.onData({ server: false });
			this.connectingToServer = false;
		});

		this.socket.on('connect_error', (error) => {
			console.error(`[Game] Socket.IO connection error:`, error);
		});

		this.socket.on('playercount', (args) => {
			this.onData({ playercount: args.count });
		});

		this.socket.on('oreBallance', (args) => {
			this.onData({ oreBallance: args });
		});

		this.socket.on('gameSessionEnded', (data) => {
			// Store ore collected data for game over screen
			this.totalOreCollected = data.oreCollected || 0;
			this.onData({ 
				oreCollected: this.totalOreCollected,
				sessionOreCollected: this.totalOreCollected,
				score: data.score || this.score 
			});
		});
	}

	disconnectFromServer() {
		if (this.socket) {
			console.log('DISCONECT FROM CLIENT');
			this.socket.disconnect();
		}
	}
	
	setupGameCoreEvents() {
		// Asteroid destroyed
		this.gameCore.events.on(GameEvents.ASTEROID_DESTROYED, (asteroid) => {
			// Play explosion animation
			// Convert GameCore 2D coordinates to THREE.js coordinates
			const threePos = CoordinateMapper.toThreeJS(asteroid.pos);
			// ExplodeAnimation expects (x, z) where z is the third parameter
			this.particlePool.push(new ExplodeAnimation(threePos.x, threePos.z, this.scene));
		});
		
		// Score changed
		this.gameCore.events.on(GameEvents.SCORE_CHANGED, (score) => {
			const scoreIncrease = score - this.score; // Calculate the increase
			this.score = score;
			this.onData({ score: this.score });
			
			// Emit score increase to server if socket is connected
			if (this.socket && this.socket.connected && scoreIncrease > 0) {
				console.log(`[Game] Score increased by ${scoreIncrease}, emitting to server. Total: ${this.score}`);
				this.socket.emit('score', scoreIncrease);
			}
		});
		
		// Ship damaged
		this.gameCore.events.on(GameEvents.SHIP_DAMAGED, (data) => {
			this.onData({ health: Math.round((data.health / this.maxShipHealth) * 100) });
		});
		
		// Ship health changed
		this.gameCore.events.on(GameEvents.SHIP_HEALTH_CHANGED, (health) => {
			this.ship.health = health;
		});
		
		// Collectible collected
		this.gameCore.events.on(GameEvents.COLLECTIBLE_COLLECTED, (data) => {
			if (data.type === 'ore') {
				this.ore = data.newOre;
				// Don't increment totalOreCollected here - only on extraction!
				this.onData({ 
					ore: this.ore,
					orePerc: Math.round((this.ore / this.maxOre) * 100)
				});
				// Update autopilot UI to show/hide platform button
				if (this.autopilotUI) {
					this.autopilotUI.updateOreStatus(this.ore > 0);
				}
				if (this.socket) {
					this.socket.emit('ore');
				}
			} else if (data.type === 'health') {
				this.onData({ health: Math.round((data.newHealth / this.maxShipHealth) * 100) });
			}
		});
		
		// Game over
		this.gameCore.events.on(GameEvents.GAME_OVER, (data) => {
			if (!this.isGameOver) {
				this.gameOver();
			}
		});
	}

	/* setWallet(address) {
		this.wallet = address;
	} */

	startGame() {
		this.resetGame();
		this.loop();
		
		// Ensure socket is connected before starting game
		if (!this.socket || !this.socket.connected) {
			console.error('[Game] Cannot start game - socket not connected!');
			console.log('[Game] Socket state:', {
				exists: !!this.socket,
				connected: this.socket?.connected,
				id: this.socket?.id
			});
			return;
		}
		
		console.log(`[Game] Starting game, emitting startGame. Socket ID: ${this.socket.id}, Connected: ${this.socket.connected}`);
		this.socket.emit('startGame');
		
		// Initialize touch controls if on mobile and they don't exist
		if (this.isMobileDevice() && !this.touchControls) {
			this.touchControls = new TouchControls(this);
		}
		
		// Show touch controls when game starts
		if (this.touchControls) {
			this.touchControls.show();
		}
		
		// Show autopilot toggle button when game starts
		if (this.autopilotUI) {
			this.autopilotUI.show(this.autopilotEnabled);
			// Initialize platform button visibility (initially false as ore is 0)
			this.autopilotUI.updateOreStatus(false);
		}

		anime({
			targets: [this.platform.position],
			y: [-75, -12],

			easing: 'easeOutQuad',
			duration: 6000,
		});

		anime({
			targets: [this.platform.rotation],
			x: [-5, -2],
			easing: 'easeOutQuad',
			duration: 6000,
		});
	}

	keyboardInit() {
		document.onkeydown = (e) => {
			/* if (!this.keyboardTouched) {
				this.keyboardTouched = true;
				this.onStart();
			} */

			if (e.target.type !== undefined) return;
			switch (e.keyCode) {
				//key A or LEFT
				case 65:
				case 37:
					this.keyLeft = true;
					break;

				//key W or UP
				case 87:
				case 38:
					this.keyUp = true;
					break;

				//key D or RIGHT
				case 68:
				case 39:
					this.keyRight = true;
					break;

				//key S or DOWN
				case 83:
				case 40:
					this.keyDown = true;
					break;

				//key Space
				case 32:
				case 75:
					this.keySpace = true;
					break;

				//key E
				case 69:
					this.keyE = true;
					break;

				//key E
				case 81:
					this.keyQ = true;
					break;

				case 70:
					this.togglePlatform();
					break;
					
				//key P - Toggle autopilot
				case 80:
					this.toggleAutopilot();
					break;
			}
			e.preventDefault();
		};

		document.onkeyup = (e) => {
			if (e.target.type !== undefined) return;
			switch (e.keyCode) {
				//key A or LEFT
				case 65:
				case 37:
					this.keyLeft = false;

					break;

				//key W or UP
				case 87:
				case 38:
					this.keyUp = false;

					break;

				//key D or RIGHT
				case 68:
				case 39:
					this.keyRight = false;

					break;

				//key S or DOWN
				case 83:
				case 40:
					this.keyDown = false;

					break;

				//key Space
				case 75:
				case 32:
					this.keySpace = false;

					break;

				//key E
				case 69:
					this.keyE = false;
					break;

				//key Q
				case 81:
					this.keyQ = false;
					break;
			}

			e.preventDefault();
		};
	}

	createFPSDisplay() {
		// Create FPS counter overlay
		this.fpsDisplay = document.createElement('div');
		this.fpsDisplay.style.cssText = `
			position: fixed;
			top: 10px;
			right: 10px;
			background: rgba(0, 0, 0, 0.6);
			color: #0f0;
			font-family: monospace;
			font-size: 11px;
			padding: 4px 8px;
			border-radius: 4px;
			z-index: 9999;
			pointer-events: none;
			border: 1px solid rgba(0, 255, 0, 0.3);
		`;
		this.fpsDisplay.textContent = 'FPS: --';
		document.body.appendChild(this.fpsDisplay);
	}
	
	updateFPSDisplay() {
		if (this.fpsDisplay && this.currentFPS > 0) {
			// Color code: green for 60+, yellow for 45-59, red for < 45
			let color = '#0f0'; // green
			if (this.currentFPS < 45) color = '#f00'; // red
			else if (this.currentFPS < 60) color = '#ff0'; // yellow
			
			this.fpsDisplay.style.color = color;
			this.fpsDisplay.textContent = `FPS: ${this.currentFPS}`;
		}
	}

	async toggleAutopilot() {
		if (this.loadingAI) return; // Prevent multiple loads
		
		this.autopilotEnabled = !this.autopilotEnabled;
		
		if (this.autopilotEnabled) {
			// Load AI if not loaded
			if (!this.aiPlayer) {
				this.loadingAI = true;
				this.autopilotUI.show(false); // Show loading state
				
				try {
					this.aiPlayer = new AIPlayer(this.aiDifficulty);
					await this.aiPlayer.load();
					console.log('[Autopilot] AI loaded successfully');
				} catch (error) {
					console.error('[Autopilot] Failed to load AI:', error);
					this.autopilotEnabled = false;
					alert('Autopilot unavailable: AI model not found. Train a model first!');
					this.autopilotUI.hide();
					this.loadingAI = false;
					return;
				}
				
				this.loadingAI = false;
			}
		}
		
		// Reset action repeat when toggling autopilot
		if (this.autopilotEnabled) {
			this.aiActionRepeat = null;
			this.aiActionRepeatCount = 0;
		}
		
		// Update UI
		if (this.autopilotEnabled) {
			this.autopilotUI.show(true);
			console.log('[Autopilot] ENGAGED - AI is now controlling the ship (K=2 action repeat)');
		} else {
			this.autopilotUI.show(false);
			console.log('[Autopilot] DISENGAGED - Manual control restored');
		}
	}

	handleResize() {
		if (!this.canvas || !this.renderer || !this.camera) return;
		
		// Get actual canvas element size from DOM (respects CSS 100% width/100vh height)
		let rect = this.canvas.getBoundingClientRect();
		let newWidth = rect.width;
		let newHeight = rect.height;
		
		// Validate the size - if invalid, use viewport dimensions as fallback
		if (newWidth <= 0 || newHeight <= 0 || newWidth > 5000 || newHeight > 5000) {
			newWidth = window.innerWidth;
			newHeight = window.innerHeight;
			
			// Cap to reasonable maximums
			const maxWidth = 1920;
			const maxHeight = 1080;
			if (newWidth > maxWidth) newWidth = maxWidth;
			if (newHeight > maxHeight) newHeight = maxHeight;
		}
		
		// Only update if size actually changed (with small tolerance for floating point)
		if (Math.abs(newWidth - this.screenWidth) > 1 || Math.abs(newHeight - this.screenHeight) > 1) {
			this.screenWidth = newWidth;
			this.screenHeight = newHeight;
			
			// Update renderer size
			this.renderer.setSize(this.screenWidth, this.screenHeight);
			
			// Update camera aspect ratio
			this.camera.aspect = this.screenWidth / this.screenHeight;
			this.camera.updateProjectionMatrix();
			
			// Recalculate world bounds
			const vFOV = (this.camera.fov * Math.PI) / 180;
			const height = 2 * Math.tan(vFOV / 2) * Math.abs(this.camera.position.y);
			const width = height * this.camera.aspect;
			this.maxPos = new THREE.Vector2(width / 2, height / 2);
			
			// Update GameCore world bounds if it exists
			if (this.gameCore) {
				this.gameCore.updateWorldBounds(width, height);
			}
			
			console.log('Canvas resized to:', this.screenWidth, 'x', this.screenHeight);
		}
	}

	onresize() {
		// Legacy 2D canvas resize handler (kept for compatibility)
		// For THREE.js, use handleResize() instead
		this.handleResize();
	}

	particleInit() {
		//this.particlePool = new Pool(Particle, 100);
		//this.particles = [];
		//this.particlePool.push(new ExplodeAnimation(0, 0, this.scene));
	}

	starsInit() {
		this.stars_foreground = new Stars(this.maxPos, 0.02, this.scene);
		this.stars_background = new Stars(this.maxPos, 0.08, this.scene);
	}

	bulletInit() {
		this.bulletPool = new Pool(Bullet, 200, this.models[4]);
		this.bullets = [];
	}

	dropItemsInit() {
		this.dropItemsPool = new Pool(DropItem, 50);
		this.dropItems = [];
	}

	asteroidInit() {
		this.asteroidPool = new Pool(Asteroid, 30);
		this.asteroids = [];
	}

	shipInit(shipModel) {
		this.ship = new Ship(0, 0, shipModel, this.maxShipHealth);
		this.ship.obb = new OBB(); //.fromBox3(new Box3().setFromObject(this.ship.mesh));

		// MEASURED from actual ship mesh geometry (see logs above)
		// Old values: (1.5, 20, 2.8) were way too large!
		// New values measured: halfWidth=0.756, halfHeight=0.371, halfDepth=1.646
		this.ship.obb.halfSize.copy(new THREE.Vector3(0.756, 0.371, 1.646));
		
		// Set OBB center to match mesh center offset
		// Mesh is shifted backward (Z=-0.252) from ship position origin
		// This aligns the collision box with the actual ship geometry
		this.ship.obb.center.copy(new THREE.Vector3(0.022, 0.001, -0.252));
		
		//geometry.userData.obb.halfSize.copy( size ).multiplyScalar( 0.5 );
		this.ship.astBox = new OBB();
		
		// === VISUAL DEBUG: Create OBB collision box visualizer ===
		if (GameConstants.SHOW_COLLISION_DEBUG) {
			const boxGeometry = new THREE.BoxGeometry(
				this.ship.obb.halfSize.x * 2,  // width
				this.ship.obb.halfSize.y * 2,  // height
				this.ship.obb.halfSize.z * 2   // depth
			);
			const boxMaterial = new THREE.MeshBasicMaterial({
				color: 0x00ff00,        // Green color
				wireframe: true,        // Show as wireframe
				transparent: true,
				opacity: 0.5            // Semi-transparent
			});
			this.ship.obbHelper = new THREE.Mesh(boxGeometry, boxMaterial);
			this.scene.add(this.ship.obbHelper);
			
			console.log('[DEBUG] Ship OBB visualizer added (green wireframe box)');
		}

		this.scene.add(this.ship.particles);
		this.scene.add(this.ship.mesh);
	}

	platformInit() {
		this.scene.add(this.platform);
		this.platform.position.y = -1;

		anime({
			targets: [this.platform.rotation],
			y: MathUtils.degToRad(360),
			easing: 'linear',
			duration: 10000,
			loop: true,
		});
	}

	togglePlatform() {
		if (this.ore <= 0) return;

		if (this.platformUp) {
			this.platformUp = false;

			anime({
				targets: [this.platform.position],
				y: [-12],
				easing: 'easeOutQuad',
				duration: 5000,
				complete: () => {
					this.onData({ platformUp: this.platformUp });
				},
			});

			anime({
				targets: [this.platform.rotation],
				x: [-2],
				easing: 'easeOutQuad',
				duration: 5000,
			});
		} else {
			this.platformUp = true;

			anime({
				targets: [this.platform.position],
				y: [-1],
				easing: 'easeOutQuad',
				duration: 5000,
				complete: () => {
					this.onData({ platformUp: this.platformUp });
				},
			});

			anime({
				targets: [this.platform.rotation],

				x: [0],
				easing: 'easeOutQuad',
				duration: 5000,
			});
		}
	}

	loop(time) {
		if (time - this.lastFrameTime < this.FRAME_MIN_TIME) {
			//skip the frame if the call is too early
			this.animationFrameId = requestAnimationFrame((time) => {
				this.loop(time);
			});

			return; // return as there is nothing to do
		}
		this.lastFrameTime = time; // remember the time of the rendered frame
		
		// FPS monitoring - AFTER throttle check to measure actual game updates
		if (!this.fpsStartTime) this.fpsStartTime = time;
		this.fpsFrameCount++;
		if (time - this.fpsStartTime >= 1000) {
			this.currentFPS = Math.round(this.fpsFrameCount * 1000 / (time - this.fpsStartTime));
			console.log(`🎮 Game Updates/sec: ${this.currentFPS} | Frame Min Time: ${this.FRAME_MIN_TIME.toFixed(2)}ms | Target: ${(1000/this.FRAME_MIN_TIME).toFixed(0)} FPS`);
			this.updateFPSDisplay(); // Update on-screen counter
			this.fpsFrameCount = 0;
			this.fpsStartTime = time;
		}

		if (this.useGameCore && this.gameCore) {
			// NEW: Use GameCore for game logic
			// Only update when game is actually running (same guard as original code)
			if (!this.ship.idle) {
				// Get input - either from AI or keyboard
				let inputState;
				if (this.autopilotEnabled && this.aiPlayer && this.aiPlayer.isLoaded) {
					// Action repeat K=2 (matches training environment)
					// Get new action every ACTION_REPEAT frames, otherwise repeat last action
					if (this.aiActionRepeat === null || this.aiActionRepeatCount >= this.ACTION_REPEAT) {
						this.aiActionRepeat = this.aiPlayer.getAction(this.gameCore);
						this.aiActionRepeatCount = 0;
					}
					this.aiActionRepeatCount++;
					
					// Use repeated action
					const aiAction = this.aiActionRepeat;
					inputState = {
						left: aiAction.left || false,
						right: aiAction.right || false,
						up: aiAction.up || false,
						down: false, // AI doesn't use down
						space: aiAction.space || false,
						q: aiAction.q || false,
						e: aiAction.e || false
					};
				} else {
					// Use keyboard input (human control - no action repeat)
					inputState = {
						left: this.keyLeft,
						right: this.keyRight,
						up: this.keyUp,
						down: this.keyDown,
						space: this.keySpace,
						q: this.keyQ,
						e: this.keyE,
						// Mobile: Variable rotation intensity (0.0 to 1.0)
						rotationIntensity: this.rotationIntensity,
						rotationDirection: this.rotationDirection
					};
				}
				
				// Update input state in GameCore
				this.gameCore.setInput(inputState);
				
				// Update game logic via GameCore
				this.gameCore.update();
				
				// Sync THREE.js objects from GameCore state
				this.syncFromGameCore();
				
				// Update shipOnPlatform state
				this.gameCore.shipOnPlatform = this.shipOnPlatform;
			}
			
			// Platform collision check (not handled by GameCore, uses THREE.js objects)
			this.checkShipPlatformCollisions();
		} else {
			// OLD: Use original logic (fallback)
			this.updateShip();
			this.updateBullets();
			this.updateAsteroids();
			this.updateDropItems();
			this.checkCollisions();
		}
		
		// Update visual-only effects (always)
		this.updateParticles();

		// Render
		this.renderer.render(this.scene, this.camera);

		// Render ship, bullets, asteroids (always)
		this.renderShip();
		this.renderBullets();
		this.renderAsteroids();

		// Update stars
		const shipState = this.useGameCore && this.gameCore ? 
			this.gameCore.getShip() : null;
		if (shipState) {
			const threePos = CoordinateMapper.toThreeJS(shipState.pos);
			this.stars_foreground.update(threePos.x * -1, threePos.z * -1, 0.07);
			this.stars_background.update(threePos.x * -1, threePos.z * -1, 0.1);
		} else {
			this.stars_foreground.update(this.ship.pos.x * -1, this.ship.pos.z * -1, 0.07);
			this.stars_background.update(this.ship.pos.x * -1, this.ship.pos.z * -1, 0.1);
		}
		
		// render the frame
		this.animationFrameId = requestAnimationFrame((time) => {
			this.loop(time);
		});
	}
	
	syncFromGameCore() {
		this.syncShip();
		this.syncAsteroids();
		this.syncBullets();
		this.syncDropItems();
	}
	
	syncShip() {
		const shipState = this.gameCore.getShip();
		if (!shipState) return;
		
		const threePos = CoordinateMapper.toThreeJS(shipState.pos);
		const threeVel = CoordinateMapper.velToThreeJS(shipState.vel);
		
		// Update THREE.js ship
		this.ship.pos.set(threePos.x, threePos.y, threePos.z);
		this.ship.angle = shipState.angle;
		this.ship.vel.set(threeVel.x, threeVel.y, threeVel.z);
		this.ship.health = shipState.health;
		this.ship.speed = shipState.speed;
		this.ship.bulletDelay = shipState.bulletCooldown;
		
		// Update mesh position and rotation
		// CRITICAL: Set rotation BEFORE position to ensure quaternion updates
		this.ship.mesh.rotation.y = shipState.angle;
		this.ship.mesh.position.copy(this.ship.pos);
		
		// Force quaternion update (THREE.js should do this automatically, but ensure it)
		this.ship.mesh.updateMatrix();
		this.ship.mesh.updateMatrixWorld();
	}
	
	syncAsteroids() {
		const asteroidsState = this.gameCore.getAsteroids();
		
		// Create map of existing THREE.js asteroids by GameCore ID
		const existingMap = new Map();
		for (const threeAsteroid of this.asteroids) {
			if (threeAsteroid.gameCoreId) {
				existingMap.set(threeAsteroid.gameCoreId, threeAsteroid);
			}
		}
		
		// Track which IDs we've seen
		const seenIds = new Set();
		
		// Update/create asteroids
		for (const asteroidState of asteroidsState) {
			seenIds.add(asteroidState.id);
			let threeAsteroid = existingMap.get(asteroidState.id);
			
			if (!threeAsteroid) {
				// Create new THREE.js asteroid
				threeAsteroid = this.createAsteroidMeshForGameCore(asteroidState);
				if (!threeAsteroid) {
					console.warn('[syncAsteroids] Failed to create asteroid mesh (pool empty?)');
					continue;
				}
				threeAsteroid.gameCoreId = asteroidState.id;
				this.asteroids.push(threeAsteroid);
				// Debug logs removed - no console spam
			}
			
			// Update position and rotation
			const threePos = CoordinateMapper.toThreeJS(asteroidState.pos);
			
			// CRITICAL: Update both pos and mesh.position from GameCore
			// Never copy mesh.position back to pos - GameCore is the source of truth!
			threeAsteroid.pos.set(threePos.x, threePos.y, threePos.z);
			threeAsteroid.mesh.position.set(threePos.x, threePos.y, threePos.z);
			
			threeAsteroid.mesh.rotation.x = asteroidState.rotation;
			
			// CRITICAL: Update velocity from GameCore and ensure it's synced
			// This prevents Asteroid.update() from moving asteroids incorrectly
			const threeVel = CoordinateMapper.velToThreeJS(asteroidState.vel);
			threeAsteroid.vel.set(threeVel.x, threeVel.y, threeVel.z);
			
			// CRITICAL: Update collision box position (astBox is used for collision detection)
			// The astBox is a Sphere created from mesh.position, so we need to recreate it
			threeAsteroid.astBox = new THREE.Sphere(threeAsteroid.mesh.position, asteroidState.radius);
			
			// CRITICAL: Update matrices (original Asteroid.update() does this)
			threeAsteroid.mesh.updateMatrix();
			threeAsteroid.mesh.updateMatrixWorld();
			
			// Update collision helper position
			if (threeAsteroid.collisionHelper) {
				threeAsteroid.collisionHelper.position.copy(threeAsteroid.mesh.position);
			}
			
			// Update health for visual feedback
			threeAsteroid.health = asteroidState.health;
		}
		
		// Remove asteroids that no longer exist in GameCore
		for (let i = this.asteroids.length - 1; i >= 0; i--) {
			const threeAsteroid = this.asteroids[i];
			if (threeAsteroid.gameCoreId && !seenIds.has(threeAsteroid.gameCoreId)) {
				this.scene.remove(threeAsteroid.mesh);
				// Remove collision helper if it exists
				if (threeAsteroid.collisionHelper) {
					this.scene.remove(threeAsteroid.collisionHelper);
					threeAsteroid.collisionHelper.geometry.dispose();
					threeAsteroid.collisionHelper.material.dispose();
					threeAsteroid.collisionHelper = null;
				}
				this.asteroids.splice(i, 1);
				if (this.asteroidPool) {
					this.asteroidPool.disposeElement(threeAsteroid);
				}
			}
		}
	}
	
	createAsteroidMeshForGameCore(asteroidState) {
		// Reuse existing asteroid creation logic
		const a = this.asteroidPool.getElement();
		if (!a) return null;
		
		a.type = asteroidState.type || 'b';
		a.radius = asteroidState.radius;
		
		// Set color based on asteroid type (matching Legacy resolveAsteroidType logic)
		if (a.type === 'boss') {
			// Boss asteroids are randomly blue or gold (matching Game.js lines 1621 and 1625)
			const colors = [0x01bdff, 0xff9114]; // Blue and gold/orange
			a.color = colors[Math.floor(Math.random() * colors.length)];
		} else {
			a.color = this.asteroidColor;
		}
		
		a.health = asteroidState.health;
		a.score = asteroidState.score;
		a.dammage = asteroidState.damage;
		
		// Clone mesh
		const mesh = this.meteorModels[Math.floor(Math.random() * this.meteorModels.length)].children[0].clone();
		const mat = mesh.material.clone();
		mesh.material = mat;
		
		a.mesh = mesh;
		a.mesh.material.color.set(a.color);
		a.mesh.scale.set(a.radius, a.radius, a.radius);
		a.astBox = new THREE.Sphere(a.mesh.position, a.radius);
		
		// === VISUAL DEBUG: Create asteroid collision sphere visualizer ===
		if (GameConstants.SHOW_COLLISION_DEBUG) {
			const sphereGeometry = new THREE.SphereGeometry(a.radius, 16, 16);
			const sphereMaterial = new THREE.MeshBasicMaterial({
				color: asteroidState.type === 'boss' ? 0xff0000 : 0xffff00,  // Red for boss, yellow for others
				wireframe: true,
				transparent: true,
				opacity: 0.3
			});
			a.collisionHelper = new THREE.Mesh(sphereGeometry, sphereMaterial);
			this.scene.add(a.collisionHelper);
		}
		
		// Set position
		const threePos = CoordinateMapper.toThreeJS(asteroidState.pos);
		a.pos = new Vector3(threePos.x, threePos.y, threePos.z);
		a.mesh.position.copy(a.pos);
		if (a.collisionHelper) {
			a.collisionHelper.position.copy(a.pos);
		}
		
		// Set velocity (for visual effects if needed)
		const threeVel = CoordinateMapper.velToThreeJS(asteroidState.vel);
		a.vel = new Vector3(threeVel.x, threeVel.y, threeVel.z);
		
		this.scene.add(a.mesh);
		
		return a;
	}
	
	syncBullets() {
		const bulletsState = this.gameCore.getBullets();
		
		// Create map of existing THREE.js bullets by GameCore ID
		const existingMap = new Map();
		for (const threeBullet of this.bullets) {
			if (threeBullet.gameCoreId) {
				existingMap.set(threeBullet.gameCoreId, threeBullet);
			}
		}
		
		// Track which IDs we've seen
		const seenIds = new Set();
		
		// Update/create bullets
		for (const bulletState of bulletsState) {
			seenIds.add(bulletState.id);
			let threeBullet = existingMap.get(bulletState.id);
			
			if (!threeBullet) {
				// Create new THREE.js bullet
				threeBullet = this.createBulletMeshForGameCore(bulletState);
				if (!threeBullet) continue;  // Pool might be empty
				threeBullet.gameCoreId = bulletState.id;
				this.bullets.push(threeBullet);
			}
			
			// Update position
			const threePos = CoordinateMapper.toThreeJS(bulletState.pos);
			threeBullet.pos.set(threePos.x, threePos.y, threePos.z);
			threeBullet.mesh.position.copy(threeBullet.pos);
			
			// Update velocity (for rendering)
			const threeVel = CoordinateMapper.velToThreeJS(bulletState.vel);
			threeBullet.vel.set(threeVel.x, threeVel.y, threeVel.z);
			
			// Store quaternion for renderBullets (it uses this)
			// Create quaternion from angle
			const quaternion = new THREE.Quaternion().setFromAxisAngle(
				new THREE.Vector3(0, 1, 0),
				bulletState.angle
			);
			threeBullet.shipQuanterion = quaternion;
			threeBullet.mesh.rotation.y = bulletState.angle;
		}
		
		// Remove bullets that no longer exist in GameCore
		for (let i = this.bullets.length - 1; i >= 0; i--) {
			const threeBullet = this.bullets[i];
			if (threeBullet.gameCoreId && !seenIds.has(threeBullet.gameCoreId)) {
				this.scene.remove(threeBullet.mesh);
				this.bullets.splice(i, 1);
				if (this.bulletPool) {
					this.bulletPool.disposeElement(threeBullet);
				}
			}
		}
	}
	
	createBulletMeshForGameCore(bulletState) {
		const b = this.bulletPool.getElement();
		if (!b) return null;
		
		const threePos = CoordinateMapper.toThreeJS(bulletState.pos);
		b.pos = new Vector3(threePos.x, threePos.y, threePos.z);
		
		// Create quaternion from angle
		const quaternion = new THREE.Quaternion().setFromAxisAngle(
			new THREE.Vector3(0, 1, 0),
			bulletState.angle
		);
		b.shipQuanterion = quaternion.clone();
		b.mesh.rotation.y = bulletState.angle;
		b.mesh.position.copy(b.pos);
		
		this.scene.add(b.mesh);
		
		return b;
	}
	
	syncDropItems() {
		const dropItemsState = this.gameCore.getDropItems();
		
		// Create map of existing THREE.js drop items by GameCore ID
		const existingMap = new Map();
		for (const threeItem of this.dropItems) {
			if (threeItem.gameCoreId) {
				existingMap.set(threeItem.gameCoreId, threeItem);
			}
		}
		
		// Track which IDs we've seen
		const seenIds = new Set();
		
		// Update/create drop items
		for (const itemState of dropItemsState) {
			seenIds.add(itemState.id);
			let threeItem = existingMap.get(itemState.id);
			
			if (!threeItem) {
				// Create new THREE.js drop item
				threeItem = this.createDropItemMeshForGameCore(itemState);
				if (!threeItem) continue;  // Pool might be empty
				threeItem.gameCoreId = itemState.id;
				this.dropItems.push(threeItem);
			}
			
			// Update position
			const threePos = CoordinateMapper.toThreeJS(itemState.pos);
			threeItem.pos = new Vector3(threePos.x, threePos.y, threePos.z);
			threeItem.mesh.position.copy(threeItem.pos);
			
			// Update rotation (matching Legacy DropItem.update() behavior)
			// DropItem.update() rotates the mesh: this.mesh.rotateZ(this.rotation * 0.6)
			threeItem.update();
		}
		
		// Remove drop items that no longer exist in GameCore
		for (let i = this.dropItems.length - 1; i >= 0; i--) {
			const threeItem = this.dropItems[i];
			if (threeItem.gameCoreId && !seenIds.has(threeItem.gameCoreId)) {
				this.scene.remove(threeItem.mesh);
				this.dropItems.splice(i, 1);
				if (this.dropItemsPool) {
					this.dropItemsPool.disposeElement(threeItem);
				}
			}
		}
	}
	
	createDropItemMeshForGameCore(itemState) {
		const item = this.dropItemsPool.getElement();
		if (!item) return null;
		
		item.type = itemState.type;
		
		const threePos = CoordinateMapper.toThreeJS(itemState.pos);
		item.pos = new Vector3(threePos.x, threePos.y, threePos.z);
		
		// Load appropriate model
		let model;
		if (itemState.type === 'ore') {
			model = this.models[3];  // ore.gltf
		} else if (itemState.type === 'health') {
			model = this.models[7];  // health.gltf
		} else {
			return null;
		}
		
		// Clone mesh from model
		const mesh = model.scene.children[0].clone();
		item.mesh = mesh;
		item.mesh.position.copy(item.pos);
		item.astBox = new THREE.Box3().setFromObject(item.mesh);
		
		this.scene.add(item.mesh);
		
		return item;
	}

	updateShip() {
		this.ship.update();
		//this.shipHelper.update();

		if (this.ship.idle) return;

		if (this.keySpace && !this.shipOnPlatform)
			this.ship.shoot(() => {
				this.generateShot();
			});

		if (this.keyLeft) this.ship.angle += 0.11;
		if (this.keyRight) this.ship.angle -= 0.11;

		if (this.keyUp) {
			this.ship.speed += 0.006;
		}

		if (this.keyDown) {
			this.ship.speed = 0;
			this.ship.vel.multiplyScalar(0.9);
		}

		if (!this.keyUp && !this.keyDown) {
			this.ship.speed = 0;
			this.ship.vel.multiplyScalar(0.98);
		}

		if (this.keyE) {
			const accelE = new Vector3(1, 0, 0).applyQuaternion(this.ship.mesh.quaternion).multiplyScalar(0.03);
			this.ship.vel.add(accelE);
		}

		if (this.keyQ) {
			const accelE = new Vector3(-1, 0, 0).applyQuaternion(this.ship.mesh.quaternion).multiplyScalar(0.03);
			this.ship.vel.add(accelE);
		}

		this.ship.speed = Math.min(Math.max(this.ship.speed, 0), 3);

		//this.generateThrustParticle();
	}

	/* screenXY(obj) {
		var width = this.screenWidth,
			height = this.screenHeight;
		var widthHalf = width / 2,
			heightHalf = height / 2;

		var pos = obj.position.clone();
		pos.project(this.camera);
		pos.x = pos.x * widthHalf + widthHalf;
		pos.y = -(pos.y * heightHalf) + heightHalf;

		return pos;
	} */

	renderShip() {
		if (this.ship.idle) return;
		
		// === UPDATE OBB VISUALIZER ===
		if (this.ship.obbHelper) {
			// Position the helper at ship position + OBB center offset
			this.ship.obbHelper.position.copy(this.ship.mesh.position);
			this.ship.obbHelper.position.add(
				new THREE.Vector3(
					this.ship.obb.center.x,
					this.ship.obb.center.y,
					this.ship.obb.center.z
				).applyQuaternion(this.ship.mesh.quaternion)
			);
			// Match ship rotation
			this.ship.obbHelper.rotation.copy(this.ship.mesh.rotation);
		}
		
		// When using GameCore, physics is handled by GameCore and synced via syncShip()
		// This method should only handle visual updates in that case
		if (!this.useGameCore) {
			// Original physics logic (only when NOT using GameCore)
			let acceleration = 0.03;

			const accelV = new Vector3(0, 0, -1).applyQuaternion(this.ship.mesh.quaternion).multiplyScalar(acceleration * this.ship.speed);
			this.ship.vel.add(accelV);

			if (this.ship.vel.length() > 0.2) {
				this.ship.vel.normalize().multiplyScalar(0.2);
			}

			this.ship.mesh.position.set(this.ship.pos.x, 0, this.ship.pos.z);
			this.ship.mesh.rotation.y = this.ship.angle;

			let pos = this.ship.mesh.position;

			if (pos.x > this.maxPos.x) {
				this.ship.pos.setX(this.ship.pos.x * -1);
			} else if (pos.x < -this.maxPos.x) {
				this.ship.pos.setX(this.ship.pos.x * -1);
			}

			if (pos.z > this.maxPos.y) {
				this.ship.pos.setZ(this.ship.pos.z * -1);
			} else if (pos.z < -this.maxPos.y) {
				this.ship.pos.setZ(this.ship.pos.z * -1);
			}
		}
		// When useGameCore is true, syncShip() has already updated mesh position
	}

	generateThrustParticle() {
		var p = this.particlePool.getElement();

		//if the particle pool doesn't have more elements, will return 'null'.

		if (!p) return;

		p.radius = Math.random() * 3 + 2;
		p.color = '#FFF';
		p.lifeSpan = 80;
		p.pos.setXY(this.ship.pos.getX() + Math.cos(this.ship.angle) * -14, this.ship.pos.getY() + Math.sin(this.ship.angle) * -14);
		p.vel.setLength(this.keyUp ? 8 : 4 / p.radius);
		p.vel.setAngle(this.ship.angle + (1 - Math.random() * 2) * (Math.PI / 18));
		p.vel.mul(-1);

		//particles[particles.length] = p; same as: particles.push(p);

		this.particles[this.particles.length] = p;
	}

	updateParticles() {
		var pCount = this.particlePool.length;

		while (pCount--) {
			this.particlePool[pCount].update();
			if (this.particlePool[pCount].blacklisted) this.particlePool.splice(pCount, 1);
		}
		/* var i = this.particles.length - 1;

		for (i; i > -1; --i) {
			var p = this.particles[i];

			if (p.blacklisted) {
				p.reset();

				this.particles.splice(this.particles.indexOf(p), 1);
				this.particlePool.disposeElement(p);

				continue;
			}

			p.update();
		} */
	}

	updateAsteroids() {
		// When using GameCore, asteroid physics is handled by GameCore
		// This method should only update physics when NOT using GameCore
		if (this.useGameCore) {
			return;  // Asteroid positions are synced from GameCore, not updated here
		}
		
		var i = this.asteroids.length - 1;

		for (i; i > -1; --i) {
			var a = this.asteroids[i];

			if (a.blacklisted) {
				a.reset();

				this.scene.remove(a.mesh);
				// Remove collision helper if it exists
				if (a.collisionHelper) {
					this.scene.remove(a.collisionHelper);
					a.collisionHelper.geometry.dispose();
					a.collisionHelper.material.dispose();
					a.collisionHelper = null;
				}
				this.asteroids.splice(this.asteroids.indexOf(a), 1);
				this.asteroidPool.disposeElement(a);

				continue;
			}

			a.update();
		}

		if (this.asteroids.length <= 7 + this.score / 1000) {
			this.generateAsteroid(this.maxPos.x, this.maxPos.y, 1.2, 'b');
		}
	}

	generateAsteroid(x, z, radius, type, color) {
		var a = this.asteroidPool.getElement();

		if (!a) return;

		a.type = type;
		a.radius = radius;

		if (color) {
			a.color = color;
		} else {
			a.color = this.asteroidColor;
		}

		if (type === 'boss') {
			a.health = 250;
			a.score = 250;
			a.dammage = 50;
		} else {
			a.health = type === 'b' ? 50 : type === 'm' ? 20 : 10;
			a.score = type === 'b' ? 50 : type === 'm' ? 20 : 10;
			a.dammage = type === 'b' ? 25 : type === 'm' ? 15 : 10;
		}

		//spawn position
		a.pos = new Vector3(x, 0, z);

		//Speed
		let speed = Math.random() * 0.08 + 0.04 + this.score / 75000;

		//random direction
		let direction = new THREE.Vector3(Math.random() * 2 - 1, 0, Math.random() * 2 - 1).normalize();

		//Add random velocity
		let vel = direction.multiplyScalar(speed);

		//Apply
		a.vel = vel;

		//Clone mesh
		let mesh = this.meteorModels[Math.floor(Math.random() * this.meteorModels.length)].children[0].clone();
		let mat = mesh.material.clone();
		mesh.material = mat;

		a.mesh = mesh;
		a.mesh.material.color.set(a.color);

		a.mesh.scale.set(a.radius, a.radius, a.radius);
		//a.astBox = new THREE.Box3().setFromObject(a.mesh);
		a.astBox = new THREE.Sphere(a.mesh.position, a.radius);
		
		// === VISUAL DEBUG: Create asteroid collision sphere visualizer ===
		if (GameConstants.SHOW_COLLISION_DEBUG) {
			const sphereGeometry = new THREE.SphereGeometry(a.radius, 16, 16);
			const sphereMaterial = new THREE.MeshBasicMaterial({
				color: type === 'boss' ? 0xff0000 : 0xffff00,  // Red for boss, yellow for others
				wireframe: true,
				transparent: true,
				opacity: 0.3
			});
			a.collisionHelper = new THREE.Mesh(sphereGeometry, sphereMaterial);
			this.scene.add(a.collisionHelper);
		}

		a.update();
		this.scene.add(a.mesh);

		//Add to pool
		this.asteroids[this.asteroids.length] = a;
	}

	checkCollisions() {
		this.checkBulletAsteroidCollisions();

		if (!this.shipOnPlatform) {
			this.checkShipAsteroidCollisions();
		}

		this.checkShipDropCollisions();
		this.checkShipPlatformCollisions();
	}

	checkBulletAsteroidCollisions() {
		var i = this.bullets.length - 1;
		var j;

		for (i; i > -1; --i) {
			j = this.asteroids.length - 1;

			for (j; j > -1; --j) {
				var b = this.bullets[i];
				var a = this.asteroids[j];

				//a.astBox.setFromObject(a.mesh).expandByScalar(-0.2);

				b.astBox.setFromObject(b.mesh);
				var collision = a.astBox.intersectsBox(b.astBox);

				if (collision) {
					b.blacklisted = true;

					a.health -= 10;
					a.highlight();

					if (a.health <= 0) {
						//Generate ORE if tehre is one
						if (Math.round(Math.random() * 20) === Math.round(Math.random() * 20)) this.generateDropItem(a.pos, 'ore');
						if (Math.round(Math.random() * 45) === Math.round(Math.random() * 45)) this.generateDropItem(a.pos, 'health');
						a.playDestroy();
						a.blacklisted = true;

						this.destroyAsteroid(a);
					}
				}
			}
		}
	}

	checkShipAsteroidCollisions() {
		var i = this.asteroids.length - 1;

		for (i; i > -1; --i) {
			var a = this.asteroids[i];
			var s = this.ship;

			//const astBox = new THREE.Box3().setFromObject(a.mesh);
			//a.astBox.setFromObject(a.mesh);

			const shotBox = this.ship.astBox;
			//shotBox.setFromObject(this.ship.mesh);
			//shotBox.applyMatrix4(new THREE.Matrix4().invert(this.ship.mesh.matrixWorld));
			//this.shipHelper.update();
			//this.ship.mesh.updateMatrixWorld();

			shotBox.copy(this.ship.obb);
			shotBox.applyMatrix4(this.ship.mesh.matrixWorld);

			//shotBox.applyMatrix4(new THREE.Matrix4().invert(this.ship.mesh.matrixWorld));
			var collision = shotBox.intersectsSphere(a.astBox); // a.astBox.intersectsBox(shotBox);

			if (collision) {
				let health = this.ship.health - a.dammage;
				this.ship.health = health > 0 ? health : 0;
				this.onData({ health: Math.round((this.ship.health / this.ship.maxShipHealth) * 100) });

				this.destroyAsteroid(a);

				if (s.health <= 0 && this.isGameOver === false) {
					this.particlePool.push(new ExplodeAnimation(s.pos.x, s.pos.z, this.scene)); //Explode ship
					this.gameOver();
				}
			}
		}
	}

	checkShipDropCollisions() {
		var i = this.dropItems.length - 1;

		for (i; i > -1; --i) {
			var a = this.dropItems[i];
			var s = this.ship;

			//const astBox = new THREE.Box3().setFromObject(a.mesh);
			a.astBox.setFromObject(a.mesh);
			//const shotBox = new THREE.Box3().setFromObject(this.ship.mesh);

			const shotBox = s.astBox;
			shotBox.copy(this.ship.obb);
			shotBox.applyMatrix4(this.ship.mesh.matrixWorld);

			var collision = shotBox.intersectsBox3(a.astBox);

			if (collision) {
				if (a.type === 'ore') {
					if (this.ore < this.maxOre) {
						this.ore += 1;
						// Don't track session ore here - only when deposited to platform
						this.onData({ 
							ore: this.ore, 
							orePerc: Math.round((this.ore / this.maxOre) * 100)
						});
						this.socket.emit('ore');
						a.blacklisted = true;
					}
				}

				if (a.type === 'health' && this.ship.health <= this.ship.maxShipHealth) {
					this.ship.health += 30;
					if (this.ship.health > this.ship.maxShipHealth) this.ship.health = this.ship.maxShipHealth;
					this.onData({ health: Math.round((this.ship.health / this.ship.maxShipHealth) * 100) });
					a.blacklisted = true;
				}
			}
		}
	}

	checkShipPlatformCollisions() {
		var s = this.ship;

		let box = new THREE.Sphere(this.platform.position, 2);
		//let box = new Sphere().setFromObject(this.platform);
		//const shotBox = new THREE.Box3().setFromObject(this.ship.mesh);

		const shotBox = s.astBox;
		shotBox.copy(this.ship.obb);
		shotBox.applyMatrix4(this.ship.mesh.matrixWorld);

		var collision = shotBox.intersectsSphere(box);

		if (collision && !this.shipOnPlatform) {
			this.shipOnPlatform = true;

			if (this.ore > 0) {
				console.log('ship offloading start');

				this.onData({ shipOnPlatform: this.shipOnPlatform });
				this.onData({ secondsLeft: 15 });

				this.oreDropoffTimer = this.createTimer(
					15,
					(secondsLeft) => {
						console.log(secondsLeft);
						this.onData({ secondsLeft: secondsLeft });
					},
					() => {
						console.log('complete');
						// Track session ore when deposited to platform
						const oreDeposited = this.ore;
						this.totalOreCollected += oreDeposited;
						this.togglePlatform();
						this.ore = 0;
						
						// Reset GameCore ore count as well
						if (this.useGameCore && this.gameCore) {
							this.gameCore.ore = 0;
						}
						
						this.onData({ 
							ore: this.ore, 
							orePerc: Math.round((this.ore / this.maxOre) * 100),
							sessionOreCollected: this.totalOreCollected
						});
						// Update autopilot UI to hide platform button
						if (this.autopilotUI) {
							this.autopilotUI.updateOreStatus(false);
						}
						this.socket.emit('oreExtracted');
					}
				);
			}

			return;
		}

		if (!collision && this.shipOnPlatform) {
			this.shipOnPlatform = false;
			clearInterval(this.oreDropoffTimer);
			this.onData({ shipOnPlatform: this.shipOnPlatform });
			console.log('ship offloading failed');
			return;
		}
	}

	createTimer(seconds, _callback, _complete) {
		var countdownTimer = setInterval(() => {
			seconds--;
			_callback(seconds);

			if (seconds <= 0) {
				clearInterval(countdownTimer);
				_complete();
			}
		}, 1000);

		return countdownTimer;
	}

	generateShipExplosion() {
		var i = 18;

		for (i; i > -1; --i) {
			var p = this.particlePool.getElement();

			//if the particle pool doesn't have more elements, will return 'null'.

			if (!p) return;

			p.radius = Math.random() * 6 + 2;
			p.lifeSpan = 80;
			p.color = '#ff0000';
			p.vel.setLength(20 / p.radius);
			p.vel.setAngle(this.ship.angle + (1 - Math.random() * 2) * this.doublePI);
			p.pos.setXY(
				this.ship.pos.getX() + Math.cos(p.vel.getAngle()) * (this.ship.radius * 0.8),
				this.ship.pos.getY() + Math.sin(p.vel.getAngle()) * (this.ship.radius * 0.8)
			);

			this.particles[this.particles.length] = p;
		}
	}

	checkDistanceCollision(obj1, obj2) {
		var vx = obj1.pos.getX() - obj2.pos.getX();
		var vy = obj1.pos.getY() - obj2.pos.getY();
		var vec = new Vec2D(vx, vy);

		if (vec.getLength() < obj1.radius - 12 + obj2.radius - 12) {
			//Fix this
			return true;
		}

		return false;
	}

	destroyAsteroid(asteroid) {
		asteroid.blacklisted = true;
		this.particlePool.push(new ExplodeAnimation(asteroid.pos.x, asteroid.pos.z, this.scene));

		this.resolveAsteroidType(asteroid);
		this.score += asteroid.score;
		
		// Emit score to server if socket is connected
		if (this.socket && this.socket.connected) {
			console.log(`[Game] Emitting score: ${asteroid.score}, total: ${this.score}, socket ID: ${this.socket.id}`);
			this.socket.emit('score', asteroid.score);
		} else {
			console.warn(`[Game] Cannot emit score - socket not connected. Score: ${asteroid.score}, total: ${this.score}`, {
				socketExists: !!this.socket,
				socketConnected: this.socket?.connected,
				socketId: this.socket?.id
			});
		}
		this.onData({ score: this.score });
	}

	generateAsteroidExplosion(asteroid) {
		var i = 18;

		for (i; i > -1; --i) {
			var p = this.particlePool.getElement();

			//if the particle pool doesn't have more elements, will return 'null'.

			if (!p) return;

			p.radius = Math.random() * (asteroid.radius >> 2) + 2;
			p.lifeSpan = 80;
			p.color = '#ff0000';
			p.vel.setLength(20 / p.radius);
			p.vel.setAngle(this.ship.angle + (1 - Math.random() * 2) * this.doublePI);
			p.pos.setXY(
				asteroid.pos.getX() + Math.cos(p.vel.getAngle()) * (asteroid.radius * 0.8),
				asteroid.pos.getY() + Math.sin(p.vel.getAngle()) * (asteroid.radius * 0.8)
			);

			//particles[particles.length] = p; same as: particles.push(p);

			this.particles[this.particles.length] = p;
		}
	}

	resolveAsteroidType(asteroid) {
		if (Math.round(Math.random() * 35) === Math.round(Math.random() * 35)) {
			this.generateAsteroid(this.maxPos.x, this.maxPos.y, 3, 'boss', 0x01bdff);
		}

		if (Math.round(Math.random() * 45) === Math.round(Math.random() * 45)) {
			this.generateAsteroid(this.maxPos.x, this.maxPos.y, 3, 'boss', 0xff9114);
		}

		switch (asteroid.type) {
			case 'b':
				this.generateAsteroid(asteroid.pos.x, asteroid.pos.z, 0.9, 'm');
				this.generateAsteroid(asteroid.pos.x, asteroid.pos.z, 0.9, 'm');

				break;

			case 'm':
				this.generateAsteroid(asteroid.pos.x, asteroid.pos.z, 0.5, 's');
				this.generateAsteroid(asteroid.pos.x, asteroid.pos.z, 0.5, 's');
				break;
		}
	}

	renderParticles() {
		//inverse for loop = more performance.

		var i = this.particles.length - 1;

		for (i; i > -1; --i) {
			var p = this.particles[i];

			this.context.beginPath();
			this.context.strokeStyle = p.color;
			this.context.fillStyle = p.color;
			this.context.arc(p.pos.getX() >> 0, p.pos.getY() >> 0, p.radius, 0, this.doublePI);
			this.context.fill();
			this.context.closePath();
		}
	}

	renderBullets() {
		//inverse for loop = more performance.

		// When using GameCore, bullet velocity is handled by GameCore
		// This method should only update visuals in that case
		if (!this.useGameCore) {
			var i = this.bullets.length - 1;

			for (i; i > -1; --i) {
				var b = this.bullets[i];
				let speed = 0.5;
				const accelV = new Vector3(0, 0, -speed).applyQuaternion(b.shipQuanterion);
				b.vel.copy(accelV);
			}
		}
		// When useGameCore is true, syncBullets() has already set positions
	}

	updateBullets() {
		// When using GameCore, bullet physics is handled by GameCore
		// This method should only update physics when NOT using GameCore
		if (this.useGameCore) {
			return;  // Bullet positions are synced from GameCore, not updated here
		}
		
		var i = this.bullets.length - 1;

		for (i; i > -1; --i) {
			var b = this.bullets[i];

			if (b.blacklisted) {
				b.reset();

				this.scene.remove(b.mesh);
				this.bullets.splice(this.bullets.indexOf(b), 1);
				this.bulletPool.disposeElement(b);

				continue;
			}

			b.update();

			//var pos = this.screenXY(b.cube);
			let pos = b.pos;

			if (pos.x >= this.maxPos.x) {
				b.blacklisted = true;
			} else if (pos.x <= -this.maxPos.x) {
				b.blacklisted = true;
			}

			if (pos.z > this.maxPos.y) {
				b.blacklisted = true;
			} else if (pos.z < -this.maxPos.y) {
				b.blacklisted = true;
			}

			/* if (b.pos.getX() > this.screenWidth) b.blacklisted = true;
			else if (b.pos.getX() < 0) b.blacklisted = true;

			if (b.pos.getY() > this.screenHeight) b.blacklisted = true;
			else if (b.pos.getY() < 0) b.blacklisted = true; */
		}
	}

	updateDropItems() {
		var i = this.dropItems.length - 1;

		for (i; i > -1; --i) {
			var item = this.dropItems[i];

			if (item.blacklisted) {
				item.reset();

				this.scene.remove(item.mesh);
				this.dropItems.splice(this.dropItems.indexOf(item), 1);
				this.dropItemsPool.disposeElement(item);

				continue;
			}

			item.update();
		}
	}

	renderAsteroids() {
		//inverse for loop = more performance.
		if (!this.asteroids) return;
		
		// === UPDATE ASTEROID COLLISION VISUALIZERS ===
		for (let i = this.asteroids.length - 1; i >= 0; i--) {
			const a = this.asteroids[i];
			if (a.collisionHelper) {
				// Update helper position to match asteroid position
				a.collisionHelper.position.copy(a.mesh.position);
			}
		}
		
		// When using GameCore, asteroid wrapping is handled by GameCore
		// This method should only handle visual updates in that case
		if (!this.useGameCore) {
			var i = this.asteroids.length - 1;

			for (i; i > -1; --i) {
				var a = this.asteroids[i];

				var pos = a.pos;

				if (pos.x > this.maxPos.x) {
					a.pos.setX(a.pos.x * -1);
				} else if (pos.x < -this.maxPos.x) {
					a.pos.setX(a.pos.x * -1);
				}

				if (pos.z > this.maxPos.y) {
					a.pos.setZ(a.pos.z * -1);
				} else if (pos.z < -this.maxPos.y) {
					a.pos.setZ(a.pos.z * -1);
				}
			}
		}
		// When useGameCore is true, syncAsteroids() has already updated positions
	}

	generateDropItem(_pos, _type) {
		var b = this.dropItemsPool.getElement();

		//if the bullet pool doesn't have more elements, will return 'null'.
		if (!b) return;

		const pos = _pos;
		b.reset();
		b.pos.copy(pos);
		let mesh;

		//Clone mesh
		if (_type === 'ore') {
			mesh = this.models[3].scene.children[0].clone();
			let mat = mesh.material.clone();
			mesh.material = mat;
		}

		if (_type === 'health') {
			mesh = this.models[7].scene.children[0].clone();
			let mat = mesh.material.clone();
			mesh.material = mat;
		}

		b.mesh = mesh;
		b.type = _type;

		//apply rotation

		//a.mesh.scale.set(a.radius, a.radius, a.radius);
		b.astBox = new THREE.Box3().setFromObject(b.mesh);
		b.update();
		this.scene.add(b.mesh);
		b.update();

		this.dropItems[this.dropItems.length] = b;
	}

	generateShot() {
		var b = this.bulletPool.getElement();

		//if the bullet pool doesn't have more elements, will return 'null'.

		if (!b) return;

		const pos = this.ship.pos.clone();
		b.shipQuanterion = this.ship.mesh.quaternion.clone();
		b.mesh.rotation.y = this.ship.mesh.rotation.y;

		b.pos.copy(pos);

		//b.vel.set(pos).multiplyScalar(0.4);
		//b.vel.setLength(6);
		this.scene.add(b.mesh);
		//b.vel.setAngle(this.ship.angle);

		//bullets[bullets.length] = b; same as: bullets.push(b);

		this.bullets[this.bullets.length] = b;
	}

	gameOver() {
		this.ship.idle = true;
		this.isGameOver = true;
		this.onData({ score: this.score });
		this.onGameOver();
		
		// Disable autopilot when game ends
		if (this.autopilotEnabled) {
			this.autopilotEnabled = false;
			if (this.autopilotUI) {
				this.autopilotUI.hide();
			}
			this.aiActionRepeat = null;
			this.aiActionRepeatCount = 0;
			console.log('[Autopilot] Disabled on game over');
		}
		
		// Hide touch controls when game ends
		if (this.touchControls) {
			this.touchControls.hide();
		}

		setTimeout(() => {
			this.destroyGame();
			// Include ore collected data in game over state
			this.onData({ 
				gameOver: true,
				oreCollected: this.totalOreCollected,
				score: this.score
			});
			// Emit gameOver - server will save final score from GameSession.destroy()
			this.socket.emit('gameOver');
		}, 2000);
	}

	resetGame() {
		this.asteroidVelFactor = 0;
		this.totalOreCollected = 0; // Reset session ore collected for new game
		
		// Ensure autopilot is disabled when starting a new game
		this.autopilotEnabled = false;
		this.aiActionRepeat = null;
		this.aiActionRepeatCount = 0;
		if (this.autopilotUI) {
			this.autopilotUI.hide();
		}

		// Reset GameCore if using it
		if (this.useGameCore && this.gameCore) {
			this.gameCore.reset();  // This calls gameCore.init() internally
			// CRITICAL: Sync GameCore state to THREE.js immediately after reset
			// This creates THREE.js meshes for asteroids, bullets, etc.
			this.syncFromGameCore();
		} else {
			// Original reset logic
			this.resetAsteroids();
			this.resetDrops();
			this.ship.reset();
		}

		this.onData({ 
			score: 0, 
			ore: 0, 
			orePerc: 0, 
			gameStarted: true, 
			gameOver: false, 
			health: (this.ship.health / this.ship.maxShipHealth) * 100, 
			sessionOreCollected: 0 
		});

		this.isGameOver = false;
		this.ship.idle = false;
		this.score = 0;
		this.ore = 0;
		this.shipOnPlatform = false;
		this.platformUp = false;
		anime.remove(this.platform);
	}

	resetAsteroids() {
		var i = this.asteroids.length - 1;

		for (i; i > -1; --i) {
			var a = this.asteroids[i];
			a.blacklisted = true;
		}
	}

	resetDrops() {
		var i = this.dropItems.length - 1;

		for (i; i > -1; --i) {
			var a = this.dropItems[i];
			a.blacklisted = true;
		}
	}

	destroyGame() {
		// Cleanup resize listener
		if (this.handleResize) {
			window.removeEventListener('resize', this.handleResize);
		}
		
		// Cleanup FPS display
		if (this.fpsDisplay && this.fpsDisplay.parentNode) {
			this.fpsDisplay.parentNode.removeChild(this.fpsDisplay);
			this.fpsDisplay = null;
		}
		
		// Cleanup autopilot - ensure it's disabled
		this.autopilotEnabled = false;
		this.aiActionRepeat = null;
		this.aiActionRepeatCount = 0;
		if (this.aiPlayer) {
			this.aiPlayer.dispose();
			this.aiPlayer = null;
		}
		if (this.autopilotUI) {
			this.autopilotUI.hide();
			// Note: Don't destroy autopilotUI here as it's reused across games
			// this.autopilotUI.destroy();
		}
		// Cleanup touch controls
		if (this.touchControls) {
			this.touchControls.destroy();
			this.touchControls = null;
		}
		cancelAnimationFrame(this.animationFrameId);
	}
	
	isMobileDevice() {
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
			   window.innerWidth < 768;
	}
	
	// Camera zoom functionality
	updateCameraZoom(newZoom) {
		// Clamp zoom
		this.currentZoom = Math.max(this.minCameraY, Math.min(this.maxCameraY, newZoom));
		
		// Update camera position (Y axis controls zoom in perspective camera)
		this.camera.position.y = this.currentZoom;
		
		// Recalculate world bounds
		const vFOV = (this.camera.fov * Math.PI) / 180;
		const height = 2 * Math.tan(vFOV / 2) * Math.abs(this.camera.position.y);
		const width = height * this.camera.aspect;
		this.maxPos = new THREE.Vector2(width / 2, height / 2);
		
		// Update GameCore world bounds
		if (this.gameCore) {
			const bounds = CoordinateMapper.calculateWorldBounds(this.camera);
			this.gameCore.worldWidth = bounds.width;
			this.gameCore.worldHeight = bounds.height;
			this.gameCore.maxX = bounds.width / 2;
			this.gameCore.maxY = bounds.height / 2;
		}
	}
	
	// Handle mouse wheel zoom (desktop)
	handleWheelZoom(event) {
		event.preventDefault();
		const delta = event.deltaY > 0 ? 1 : -1;
		this.updateCameraZoom(this.currentZoom + delta * this.zoomSpeed);
	}

	/* ExplodeAnimation(x, y) {
		var geometry = new THREE.BufferGeometry();

		for (let i = 0; i < 500; i++) {
			var vertex = new THREE.Vector3();
			vertex.x = x;
			vertex.y = y;
			vertex.z = 0;

			geometry.vertices.push(vertex);
			dirs.push({
				x: Math.random() * movementSpeed - movementSpeed / 2,
				y: Math.random() * movementSpeed - movementSpeed / 2,
				z: Math.random() * movementSpeed - movementSpeed / 2,
			});
		}
		var material = new THREE.ParticleBasicMaterial({ size: 10, color: colors[Math.round(Math.random() * colors.length)] });
		var particles = new THREE.ParticleSystem(geometry, material);

		this.object = particles;
		//this.status = true;

		this.xDir = Math.random() * movementSpeed - movementSpeed / 2;
		this.yDir = Math.random() * movementSpeed - movementSpeed / 2;
		this.zDir = Math.random() * movementSpeed - movementSpeed / 2;

		scene.add(this.object);

		this.update = function () {
			if (this.status == true) {
				var pCount = totalObjects;
				while (pCount--) {
					var particle = this.object.geometry.vertices[pCount];
					particle.y += dirs[pCount].y;
					particle.x += dirs[pCount].x;
					particle.z += dirs[pCount].z;
				}
				this.object.geometry.verticesNeedUpdate = true;
			}
		};
	} */
}
